import { type BtnType } from '@Types/Shared/Button';
import prependHttp from 'prepend-http';
import { ArrowBig, ArrowSmall } from '@Shared/Icons';
import { Link } from '@inertiajs/react';
import { motion } from "framer-motion";

export function Button(props: BtnType) {
	const {
		label,
		url,
		style = 'primary',
		urlOpenNewTab = false,
		urlType = 'link',
		btnHasIcon = false,
		btnDecorationElement = 'long',
		primaryClassname = '',
		secondaryClassname = '',
		tertiaryClassname = '',
		aClassname = '',
		children,
		className,
		animation,
	} = props;

	const classes: string[] = [];

	const basicStyle = `group relative text-base inline-block transition-colors last:mr-0 w-fit mt-auto`;

	let positionStyle = '';

	// if parentNode as a style = text-align = center | right
	if (className) {
		if (className.includes('center')) positionStyle = '!mx-auto !mt-3';
		if (className.includes('right')) positionStyle = '!ml-auto !mt-3';
	} else {
		positionStyle = '';
	}

	if (style === 'primary') {
		classes.push(primaryClassname, basicStyle, 'px-4 py-2 tablet:px-8 tablet:py-3', positionStyle);
	}

	if (style === 'secondary') {
		classes.push(secondaryClassname, basicStyle, 'px-4 py-2 tablet:px-8 tablet:py-3', positionStyle);
	}

	if (style === 'tertiary') {
		classes.push(tertiaryClassname, basicStyle, positionStyle);
	}

	if (style === 'a') {
		classes.push(
			`${aClassname} ${btnHasIcon && btnDecorationElement === 'underline' ? ' underline' : ''}`
		);
	}

	let target = '';

	if (urlOpenNewTab) {
		target = '_blank';
	}

	// Animation with framer-motion
	const AnimatedLink = motion(Link);
	let variants;
	let transition;

	const none = {
		variants: {
			rest: {},
			hover: {},
			pressed: {}
		},
		transition: {}
	}
	
	const scale = {
		variants: {
			rest: { scale: 1 },
			hover: { scale: 1.15 },
			pressed: { scale: 1.05 },
		},
		transition: {
			type: "spring",
			damping: 5,
			stiffness: 75,
			restDelta: 0.001
		}
	}
	
	const slideup = {
		variants: {
			rest: {},
			hover: { y: -10 },
			pressed: {},
		},
		transition: {
			type: "tween",
			ease: "circOut"
		}
	}
	
	const shake = {
		variants: {
			rest: { rotate: [0, 0, 0, 0] },
			hover: { rotate: [0, 3, -3, 0] },
			pressed: {},
		},
		transition: {
			repeat: 2,
			ease: 'linear',
			duration: .2,
		}
	}
	
	if (animation) {
		switch(animation) {
			case 'scale':
				variants = scale.variants;
				transition = scale.transition;
				break;

			case 'slideup':
				variants = slideup.variants;
				transition = slideup.transition;
				break;
			
			case 'shake':
				variants = shake.variants;
				transition = shake.transition;
				break;

			default:
				variants = none.variants;
				transition = none.transition
				break;
		}
	}

	return (
		<>
			{url && (
				<>
					{urlType === 'link' ? (
						<motion.a
							variants={variants}
							initial="rest"
							whileHover="hover"
							whileTap="pressed"
							transition={transition}

							tabIndex={0}
							role='button'
							href={prependHttp(url)}
							className={`${classes.join(' ')} inline-flex items-center justify-center gap-2`}
							target={target}
						>
							{children ? (
								<>{children}</>
							) : (
								<span
									className={`${btnHasIcon && btnDecorationElement === 'underline' ? ' underline' : ''}`}
								>
									{label}
								</span>
							)}

							{btnHasIcon && (
								<>
									{btnDecorationElement === 'long' && (
										<ArrowBig
											width='20'
											className='transition-transform group-hover:translate-x-2 group-hover:duration-300 group-hover:ease-in-out'
										/>
									)}

									{btnDecorationElement === 'short' && (
										<ArrowSmall
											width='20'
											className='transition-transform group-hover:translate-x-2 group-hover:duration-300 group-hover:ease-in-out'
										/>
									)}
								</>
							)}
						</motion.a>
					) : (
						<AnimatedLink
							variants={variants}
							initial="rest"
							whileHover="hover"
							whileTap="pressed"
							transition={transition}

							href={url}
							className={`${style != 'a' ? 'inline-flex items-center justify-center gap-2' : ''} ${classes.join(' ')}`}
							target={target}
						>
							{children ? (
								<>{children}</>
							) : (
								<span
									className={`${btnHasIcon && btnDecorationElement === 'underline' ? ' underline' : ''}`}
								>
									{label}
								</span>
							)}
							{btnHasIcon && (
								<>
									{btnDecorationElement === 'long' && (
										<ArrowBig
											width='20'
											className='transition-transform group-hover:translate-x-2 group-hover:duration-300 group-hover:ease-in-out'
										/>
									)}

									{btnDecorationElement === 'short' && (
										<ArrowSmall
											width='20'
											className='transition-transform group-hover:translate-x-2 group-hover:duration-300 group-hover:ease-in-out'
										/>
									)}
								</>
							)}
						</AnimatedLink>
					)}
				</>
			)}
		</>
	);
}
